import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Info, CheckCircle2, Navigation, Car } from "lucide-react";
import { toast } from "sonner";
import { usePsStore } from "../../store/psStore";
import { psGetCheckout, psAcceptQuote, psAuthorize } from "../../lib/psApi";
import { chf } from "../../lib/format";

const REASON_LABELS = {
  EMPTY_CART: "Votre panier est vide.",
  ADDRESS_REQUIRED: "Ajoutez votre adresse de livraison pour calculer la distance.",
  PAYMENT_ESTIMATE_INCOMPLETE: "Le paiement sera disponible lorsque l'estimation des marchandises sera disponible.",
  PRICING_NOT_CONFIGURED: "L'option de livraison choisie n'a pas encore de tarif configuré.",
  PSP_NOT_CONFIGURED: "Paiement temporairement indisponible.",
};
const newIdemKey = () => "ps_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);

export default function PsCheckout() {
  const nav = useNavigate();
  const { order, recordPlaced } = usePsStore();
  const [co, setCo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const idemRef = useRef(newIdemKey());

  const load = async () => {
    if (!order?.id) { setLoading(false); return; }
    setLoading(true);
    try { setCo(await psGetCheckout(order.id)); } finally { setLoading(false); }
  };
  useEffect(() => { idemRef.current = newIdemKey(); load(); }, [order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const authorize = async () => {
    if (!order?.id || busy || !co?.payment_ready) return;
    setBusy(true);
    try {
      const acc = await psAcceptQuote(order.id);
      const res = await psAuthorize(order.id, idemRef.current, acc.accepted_quote_version);
      setCo(res.checkout);
      recordPlaced();
      toast.success(`Carte préautorisée pour ${chf(res.payment.authorized_amount_chf)}. Aucun montant encore débité.`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const reason = typeof d === "object" ? d?.reason : undefined;
      if (reason === "QUOTE_CHANGED" && d?.checkout) {
        setCo(d.checkout); idemRef.current = newIdemKey();
        toast.warning("Le tarif a changé. Vérifiez puis confirmez à nouveau.");
      } else {
        setCo(await psGetCheckout(order.id).catch(() => co));
        toast.error(REASON_LABELS[reason] || "Impossible d'autoriser pour le moment.");
      }
    } finally { setBusy(false); }
  };

  const subs = order?.sub_orders || [];
  const q = co?.quote;
  const authorized = co?.payment_status === "AUTHORIZED";

  const Section = ({ title, children }) => (<>
    <p className="text-xs font-extrabold tracking-widest mt-5 mb-2" style={{ color: "#64748b" }}>{title}</p>{children}
  </>);
  const card = { border: "1px solid #f0f0f0", background: "#fff" };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-2">
        <button onClick={() => nav("/personal-shopper/panier")} data-testid="ps-checkout-back" style={{ color: "#0b1220" }}><ArrowLeft size={24} /></button>
        <h1 className="text-xl font-extrabold" style={{ color: "#0b1220" }}>Vérifier ma commande</h1>
      </div>

      {loading ? (
        <div className="h-64 rounded-2xl animate-pulse mt-4" style={{ background: "#e9edf2" }} />
      ) : (
        <>
          <Section title="MES COURSES">
            <div className="rounded-2xl p-4" style={card}>
              {subs.map((s) => (
                <div key={s.id} className="flex justify-between py-1.5">
                  <span className="font-bold" style={{ color: "#0b1220" }}>{s.store_name}</span>
                  <span className="text-sm" style={{ color: "#64748b" }}>{s.item_count} {s.fulfillment_type === "MERCHANT_PREPARED" ? "retrait(s)" : "article(s)"}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section title="LIVRAISON">
            <button onClick={() => nav("/personal-shopper/livraison")} data-testid="ps-checkout-delivery" className="w-full flex items-center justify-between rounded-2xl p-4" style={card}>
              <span className="font-bold" style={{ color: "#0b1220" }}>{order?.delivery_mode || "Choisir un créneau"}</span>
              <span className="text-sm font-extrabold" style={{ color: "#16a34a" }}>Modifier</span>
            </button>
            <div className="flex items-start gap-2.5 rounded-2xl p-4 mt-2.5" style={{ background: "#eff6ff", border: "1px solid #bfdbfe" }} data-testid="ps-checkout-trunk-note">
              <Car size={20} color="#1d4ed8" style={{ flexShrink: 0, marginTop: 2 }} />
              <span className="text-sm leading-relaxed" style={{ color: "#1e40af" }}>
                {"Votre commande vous attend à la voiture : retrouvez votre livreur au coffre. Besoin d'une remise jusqu'à votre porte ? Ajoutez l'option « à la porte » (2.90 CHF, 3.90 dès le 2ᵉ étage)."}
              </span>
            </div>
          </Section>

          <Section title="ADRESSE">
            <button onClick={() => nav("/personal-shopper/panier")} data-testid="ps-checkout-address" className="w-full flex items-center justify-between rounded-2xl p-4" style={card}>
              <span className="font-bold truncate mr-3" style={{ color: "#0b1220" }}>{order?.pricing_address || "Ajouter une adresse"}</span>
              <span className="text-sm font-extrabold" style={{ color: "#16a34a" }}>Modifier</span>
            </button>
          </Section>

          <Section title="FRAIS PERSONAL SHOPPER">
            <div className="rounded-2xl p-4" style={card}>
              {(q?.lines || []).map((l) => (
                <div key={l.key} className="flex justify-between py-1.5">
                  <span className="text-sm" style={{ color: "#0b1220" }}>{l.label}{l.detail ? ` · ${l.detail}` : ""}</span>
                  <span className="text-sm font-bold" style={{ color: l.status === "PENDING" ? "#f59e0b" : "#0b1220" }}>{l.amount_chf == null ? "—" : chf(l.amount_chf)}</span>
                </div>
              ))}
              <div className="flex justify-between mt-2 pt-2.5" style={{ borderTop: "1px solid #e2e8f0" }}>
                <span className="text-sm font-extrabold" style={{ color: "#0b1220" }}>TOTAL SERVICES</span>
                <span className="text-lg font-extrabold" style={{ color: "#0b1220" }}>{co ? chf(co.service_total_chf) : "—"}</span>
              </div>
            </div>
          </Section>

          {co?.payment_ready ? (
            <div className="rounded-2xl p-4 mt-5" style={{ background: "#ecfdf5", border: "1px solid #a7f3d0" }} data-testid="ps-checkout-preauth">
              <div className="flex justify-between py-1"><span className="text-sm" style={{ color: "#065f46" }}>Marchandises estimées</span><span className="text-sm font-bold" style={{ color: "#065f46" }}>{chf(co.estimated_goods_amount_chf)}</span></div>
              <div className="flex justify-between py-1"><span className="text-sm" style={{ color: "#065f46" }}>Services La BASE</span><span className="text-sm font-bold" style={{ color: "#065f46" }}>{chf(co.service_total_chf)}</span></div>
              <div className="flex justify-between py-1"><span className="text-sm" style={{ color: "#065f46" }}>Réserve de sécurité</span><span className="text-sm font-bold" style={{ color: "#065f46" }}>{chf(co.preauth_reserve_chf)}</span></div>
              <div className="flex justify-between items-center mt-2 pt-2.5" style={{ borderTop: "1px solid #a7f3d0" }}>
                <span className="text-sm font-extrabold" style={{ color: "#047857" }}>PRÉAUTORISATION BANCAIRE</span>
                <span className="text-2xl font-extrabold" style={{ color: "#047857" }} data-testid="ps-checkout-preauth-amount">{chf(co.authorization_amount_chf)}</span>
              </div>
              <p className="text-xs mt-3 leading-relaxed" style={{ color: "#065f46" }}>Il s'agit d'une préautorisation bancaire et non du montant définitif encaissé. Le montant des courses sera ajusté aux achats réellement effectués.</p>
            </div>
          ) : (
            <div className="flex items-start gap-2.5 rounded-2xl p-4 mt-5" style={{ background: "#fffbeb", border: "1px solid #fde68a" }} data-testid="ps-checkout-blocked">
              <Info size={20} color="#b45309" /><span className="text-sm" style={{ color: "#92400e" }}>{REASON_LABELS[co?.reasons?.[0]] || "Paiement indisponible pour le moment."}</span>
            </div>
          )}

          {authorized ? (
            <>
              <div className="flex items-center gap-2.5 rounded-2xl p-4 mt-5" style={{ background: "#ecfdf5", border: "1px solid #a7f3d0" }} data-testid="ps-checkout-authorized">
                <CheckCircle2 size={20} color="#16a34a" /><span className="text-sm font-bold" style={{ color: "#047857" }}>Préautorisation active. Aucun montant encore débité.</span>
              </div>
              <button onClick={() => nav(`/personal-shopper/suivi/${order.track_token}?n=${order.order_number}`)} data-testid="ps-checkout-track"
                className="w-full rounded-2xl font-extrabold text-white py-4 mt-4 flex items-center justify-center gap-2" style={{ background: "#0b1220" }}>
                <Navigation size={18} /> Suivre ma commande
              </button>
            </>
          ) : (
            <button onClick={authorize} disabled={!co?.payment_ready || busy} data-testid="ps-checkout-authorize"
              className="w-full rounded-2xl font-extrabold text-white py-4 mt-5" style={{ background: !co?.payment_ready || busy ? "#cbd5e1" : "#0b1220" }}>
              {busy ? "Traitement..." : "Valider et autoriser"}
            </button>
          )}
        </>
      )}
    </div>
  );
}
